import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import NorthIcon from "@mui/icons-material/North";
import { Film, Media, Genre } from "../types/interfaces";
import RateCanvas from "./RateCanvas";
import Api from "../api/Api";
import { BASE_API_URL } from "./../api/const";
import moviesStore from "./../stores/moviesStore";
import CloseIcon from "@mui/icons-material/Close";
import DeleteFilmModal from "./DeleteFilmModal";

interface MainBodyProps {
  movies: Film[];
  loading: boolean;
}

// Тип для позиции
interface HoverPosition {
  top: number;
  left: number;
}

const MainBody: React.FC<MainBodyProps> = ({ movies, loading }) => {
  const [hoveredPoster, setHoveredPoster] = useState<string | null>(null);
  const [hoverPosition, setHoverPosition] = useState<HoverPosition>({
    top: 0,
    left: 0,
  });

  const [filmToDelete, setFilmToDelete] = useState<Film | null>(null);
  const [loadingLinks, setLoadingLinks] = useState<boolean>(false);

  const POSTER_WIDTH = 280;
  const POSTER_HEIGHT = 420;
  const OFFSET_X = 120;
  const OFFSET_Y = -200;
  const BOTTOM_MARGIN = 80;

  const handleMouseEnter = (
    event: React.MouseEvent<HTMLTableCellElement>,
    element: Film
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();

    let top = rect.top + window.scrollY + OFFSET_Y;
    let left = rect.left + window.scrollX + OFFSET_X;

    // вычисляем нижнюю границу постера
    const posterBottom = top + POSTER_HEIGHT;

    if (
      posterBottom >
      window.scrollY + window.innerHeight - BOTTOM_MARGIN + OFFSET_Y
    ) {
      top =
        window.scrollY +
        window.innerHeight -
        POSTER_HEIGHT -
        BOTTOM_MARGIN +
        OFFSET_Y;
    }

    setHoverPosition({ top, left });
    setHoveredPoster(element.logo);
  };

  const handleMouseLeave = () => {
    setHoveredPoster(null);
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );

  if (!movies.length)
    return (
      <Typography variant="body1" mt={4}>
        Нет фильмов
      </Typography>
    );

  function getSeasonsString(film: Film): string {
    if (!film.serial || !film.media) return "";

    // Фильтруем только те, у кого указан номер сезона
    const seasons = film.media
      .filter((s) => typeof s.season === "number")
      .sort((a, b) => a.season! - b.season!);

    if (seasons.length === 0) return "";

    let result: string[] = [];
    let current = seasons[0].season!;

    for (let i = 0; i < seasons.length; i++) {
      const s = seasons[i];

      // Добавляем "..." для пропущенных сезонов
      while (current < s.season!) {
        result.push("..");
        current++;
      }

      // Добавляем количество серий
      result.push(`(${s.items?.length ?? 0})`);
      current++;
    }

    return result.join(" ");
  }

function getSeasonsElements(film: Film): React.ReactNode {
  if (!film.serial || !film.media) return null;

  const seasons = film.media
    .filter((s) => typeof s.season === "number")
    .sort((a, b) => a.season! - b.season!);

  if (seasons.length === 0) return null;

  const result: React.ReactNode[] = [];
  let current = seasons[0].season!;

  for (const s of seasons) {
    const gap = s.season! - current;

    if (gap > 0) {
      if (gap > 5) {
        result.push(
          <span key={`gap-${current}`} style={{ marginLeft: 4 }}>
            ..
          </span>
        );
      } else {
        for (let i = 0; i < gap; i++) {
          result.push(
            <span key={`gap-${current + i}`} style={{ marginLeft: 4 }}>
              ..
            </span>
          );
        }
      }
    }

    result.push(
      <Tooltip
        key={`season-${s.season}`}
        title={`${s.title} — ${s.items?.length ?? 0} серий`}
        arrow
      >
        <span style={{ marginLeft: 4, cursor: "default" }}>
          ({s.items?.length ?? 0})
        </span>
      </Tooltip>
    );

    current = s.season! + 1;
  }

  return result;
}

  const handleToggleShow = async (
    film: Film,
    serviceKey: keyof typeof film.show
  ) => {
    try {
      const currentValue = film.show[serviceKey];
      const newValue = currentValue === 1 ? 0 : 1;

      await Api.post(`${BASE_API_URL}/movies/changeStatusShow`, {
        movie_id: film.id,
        service: serviceKey,
        action: newValue,
      });

      // делаем последний выполненный запрос
      moviesStore.repeatLastPortalQuery();
    } catch (err: any) {
      console.error("Не удалось изменить статус сервиса", err);

      // проверяем есть ли структура с code/message
      const msg =
        err?.response?.data?.error?.message ||
        err?.message ||
        "Ошибка при изменении статуса сервиса";

      alert(`Ошибка: ${msg}`);
    }
  };
  const handleToggleService = async (
    film: Film,
    serviceKey: keyof typeof film.service
  ) => {
    try {
      const currentValue = film.service[serviceKey];
      const newValue = currentValue === 1 ? 0 : 1;

      await Api.post(`${BASE_API_URL}/movies/changeStatusService`, {
        movie_id: film.id,
        service: serviceKey,
        action: newValue,
      });

      // делаем последний выполненный запрос
      moviesStore.repeatLastPortalQuery();
    } catch (err: any) {
      console.error("Не удалось изменить статус сервиса", err);

      // проверяем есть ли структура с code/message
      const msg =
        err?.response?.data?.error?.message ||
        err?.message ||
        "Ошибка при изменении статуса сервиса";

      alert(`Ошибка: ${msg}`);
    }
  };

  const handleMoveUp = async (filmId: number) => {
    try {
      await Api.post(`${BASE_API_URL}/movies/up`, {
        movie_id: filmId,
      });
      // тут можно обновить список фильмов или повторить запрос
      // moviesStore.repeatLastPortalQuery();

      moviesStore.repeatLastPortalQuery();
    } catch (err) {
      console.error("Ошибка при перемещении фильма вверх:", err);
      alert("Ошибка при перемещении фильма вверх");
    }
  };

  const handleDeleteFilm = (film: Film) => {
    setFilmToDelete(film); // открываем модалку
  };

  const AGE_OPTIONS = [0, 6, 12, 16, 18];

  const changeMovieRateAge = async (movieId: number, age: string) => {
    try {
      await Api.post(`${BASE_API_URL}/movies/age`, {
        movie_id: movieId,
        age,
      });

      // повторяем последний запрос списка фильмов
      moviesStore.repeatLastPortalQuery();
    } catch (err: any) {
      console.error("Не удалось изменить возрастной рейтинг", err);

      const msg =
        err?.response?.data?.error?.message ||
        err?.message ||
        "Ошибка при изменении возрастного рейтинга";

      alert(`Ошибка: ${msg}`);
    }
  };



  return (
    <Box sx={{ cursor: loadingLinks ? "progress" : "default" }}>
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        mt={2}
        sx={{ width: "100vw", position: "relative" }}
      >
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            {/* Шапка */}
            <TableHead>
              <TableRow
                sx={{
                  "& td, & th": { fontWeight: "bold", textAlign: "center" },
                }}
              >
                <TableCell
                  sx={{ width: 80, maxWidth: 80, textAlign: "center" }}
                >
                  Постер
                </TableCell>
                <TableCell
                  sx={{ width: 200, maxWidth: 200, textAlign: "center" }}
                >
                  Название
                </TableCell>
                <TableCell>Год</TableCell>
                <TableCell>Рейтинги</TableCell>
                <TableCell>Возраст</TableCell>
                <TableCell>Страны</TableCell>
                <TableCell>Жанры</TableCell>
                <TableCell>Portal ID</TableCell>
                <TableCell>Дата заливки</TableCell>
                <TableCell
                  sx={{
                    maxWidth: 100,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Дата редактирования
                </TableCell>
                <TableCell sx={{ width: 350, minWidth: 350 }}>
                  Кинотеатры
                </TableCell>
                <TableCell>Сервисы</TableCell>
              </TableRow>
            </TableHead>

            {/* Строки с фильмами */}
            <TableBody>
              {movies.map((film) => (
                <TableRow
                  key={film.id}
                  sx={{
                    "& td": {
                      padding: "2px 4px",
                      height: 40,
                    },
                  }}
                >
                  {/* Постер */}
                  <TableCell
                    onMouseEnter={(event) => handleMouseEnter(event, film)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <img
                        src={film.logo || film.background}
                        alt={film.title}
                        style={{ width: 80, height: 45, objectFit: "cover" }}
                      />
                      {/* Метка "Сериал" */}
                      {film.serial && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            backgroundColor: "yellow",
                            color: "black",
                            fontSize: "10px",
                            fontWeight: "bold",
                            padding: "2px 4px",
                            zIndex: 1,
                          }}
                        >
                          Сериал
                        </div>
                      )}
                      {/* Метка "Мультфильм" */}
                      {film.genre?.some(
                        (g: Genre) => g.name.toLowerCase() === "мультфильм"
                      ) && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            backgroundColor: "violet",
                            color: "black",
                            fontSize: "10px",
                            fontWeight: "bold",
                            padding: "2px 4px",
                            zIndex: 1,
                          }}
                        >
                          Мультфильм
                        </div>
                      )}
                      {/* Метка "Шоу" */}
                      {film.genre?.some(
                        (g: Genre) => g.name.toLowerCase() === "шоу"
                      ) && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            right: 0,
                            backgroundColor: "red",
                            color: "black",
                            fontSize: "10px",
                            fontWeight: "bold",
                            padding: "2px 4px",
                            zIndex: 1,
                          }}
                        >
                          Шоу
                        </div>
                      )}
                    </div>
                  </TableCell>

                  {/* Название */}
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold">
                      {(() => {
                        const params = new URLSearchParams({
                          id: film.id.toString(),
                        });
                        const href = `/filmdata?${params.toString()}`;
                        return (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            {film.title}{" "}
                            {film.serial ? getSeasonsElements(film) : ""}
                          </a>
                        );
                      })()}
                    </Typography>
                  </TableCell>

                  {/* Год */}
                  <TableCell>{film.year}</TableCell>

                  {/* Рейтинги */}
                  <TableCell>
                    {film.mbase_id !== 0 ? (
                      <a
                        href={`https://mbase.idc.md/film/${film.mbase_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <RateCanvas
                          rates={{
                            vote_kp: film.vote_kp,
                            rate_kp: film.rate_kp,
                            age: film.rate_age,
                            vote_imdb: film.vote_imdb,
                            rate_imdb: film.rate_imdb,
                          }}
                        />
                      </a>
                    ) : (
                      <RateCanvas
                        rates={{
                          vote_kp: film.vote_kp,
                          rate_kp: film.rate_kp,
                          age: film.rate_age,
                          vote_imdb: film.vote_imdb,
                          rate_imdb: film.rate_imdb,
                        }}
                      />
                    )}
                  </TableCell>

                  {/* Возраст */}
                  <TableCell>
                    <Tooltip
                      title={
                        <Box>
                          <div>rus: {film.age?.rus || "—"}</div>
                          <div>mpaa: {film.age?.mpaa || "—"}</div>
                          <div>alter: {film.age?.alter || "—"}</div>
                        </Box>
                      }
                      placement="right"
                      arrow
                    >
                      <FormControl size="small" fullWidth>
                        <Select
                          value={
                            film.rate_age ? film.rate_age.replace("+", "") : ""
                          }
                          displayEmpty
                          onChange={async (e) => {
                            const age = e.target.value as string;

                            if (age === (film.rate_age ?? "")) return;

                            await changeMovieRateAge(film.id, age);
                          }}
                          sx={{
                            minWidth: 60,
                            width: 60,
                            "& .MuiSelect-select": {
                              fontSize: "0.75rem",
                              padding: "2px 6px",
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                "& .MuiMenuItem-root": {
                                  fontSize: "0.75rem",
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value="">—</MenuItem>

                          {AGE_OPTIONS.map((age) => (
                            <MenuItem key={age} value={String(age)}>
                              {age}+
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Tooltip>
                  </TableCell>

                  {/* Страны */}
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{ flexWrap: "wrap" }}
                    >
                      {film.country.slice(0, 1).map((c, i) => (
                        <Chip
                          key={i}
                          label={c}
                          size="small"
                          sx={{ backgroundColor: "rgba(0,0,0,0.08)" }}
                        />
                      ))}
                      {film.country.length > 1 && (
                        <Tooltip
                          title={
                            <Box>
                              {film.country.slice(1).map((country, index) => (
                                <div key={index}>{country}</div>
                              ))}
                            </Box>
                          }
                          arrow
                        >
                          <Chip
                            label={`+${film.country.length - 1}`}
                            size="small"
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>

                  {/* Жанры */}
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{ flexWrap: "wrap" }}
                    >
                      {(() => {
                        // Копируем массив жанров
                        let genres = [...film.genre];

                        // Если есть Ultra HD — выносим его на первое место
                        const ultraHDIndex = genres.findIndex(
                          (g) => g.name === "Ultra HD"
                        );
                        if (ultraHDIndex !== -1) {
                          const ultraHD = genres.splice(ultraHDIndex, 1)[0];
                          genres.unshift(ultraHD); // добавляем в начало
                        }

                        return genres.slice(0, 3).map((g, i) => (
                          <Chip
                            key={g.id ?? i}
                            label={g.name}
                            size="small"
                            sx={{
                              backgroundColor:
                                g.name === "Ultra HD"
                                  ? "rgba(255, 215, 0, 0.25)" // золотистый фон для Ultra HD
                                  : "rgba(0,0,0,0.08)",
                            }}
                          />
                        ));
                      })()}

                      {film.genre.length > 3 && (
                        <span>
                          <Tooltip
                            title={
                              <Box>
                                {film.genre.slice(3).map((genre, index) => (
                                  <div key={index}>{genre.name}</div>
                                ))}
                              </Box>
                            }
                            arrow
                          >
                            <Chip
                              label={`+${film.genre.length - 3}`}
                              size="small"
                            />
                          </Tooltip>
                        </span>
                      )}
                    </Stack>
                  </TableCell>

                  {/* Portal ID */}
                  <TableCell>{film.id}</TableCell>

                  {/* Дата заливки */}
                  <TableCell>{film.created_at}</TableCell>

                  {/* Дата редактирования */}
                  <TableCell>{film.updated_at}</TableCell>

                  {/* Кинотеатры */}
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing={0}
                      sx={{ flexWrap: "wrap" }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        color={
                          film.service.portal === 1 ? "primary" : "inherit"
                        }
                        sx={{
                          minWidth: 50,
                          padding: "6px 6px",
                          fontSize: "0.75rem",
                          lineHeight: 1.2,
                        }}
                        onClick={() => handleToggleService(film, "portal")}
                      >
                        Portal
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color={
                          film.service.premier === 1 ? "primary" : "inherit"
                        }
                        sx={{
                          minWidth: 50,
                          padding: "6px 6px",
                          fontSize: "0.75rem",
                          lineHeight: 1.2,
                        }}
                        onClick={() => handleToggleService(film, "premier")}
                      >
                        Premier
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color={film.service.wink === 1 ? "primary" : "inherit"}
                        sx={{
                          minWidth: 50,
                          padding: "6px 6px",
                          fontSize: "0.75rem",
                          lineHeight: 1.2,
                        }}
                        onClick={() => handleToggleService(film, "wink")}
                      >
                        Wink
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color={film.service.tsv === 1 ? "primary" : "inherit"}
                        sx={{
                          minWidth: 50,
                          padding: "6px 6px",
                          fontSize: "0.75rem",
                          lineHeight: 1.2,
                        }}
                        onClick={() => handleToggleService(film, "tsv")}
                      >
                        TSV
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color={film.service.demo === 1 ? "primary" : "inherit"}
                        sx={{
                          minWidth: 50,
                          padding: "6px 6px",
                          fontSize: "0.75rem",
                          lineHeight: 1.2,
                        }}
                        onClick={() => handleToggleService(film, "demo")}
                      >
                        demo
                      </Button>
                      <IconButton
                        onClick={() => handleMoveUp(film.id)}
                        size="small"
                      >
                        <ArrowUpwardIcon sx={{ color: "green", ml: 1 }} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteFilm(film)}
                        size="small"
                      >
                        <CloseIcon sx={{ color: "red", ml: 1 }} />
                      </IconButton>
                    </Stack>
                  </TableCell>

                  {/* Сервисы */}
                  <TableCell
                    sx={{
                      minWidth: 180,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={0}
                      sx={{ flexWrap: "wrap" }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        color={film.show.media === 1 ? "primary" : "inherit"}
                        sx={{
                          minWidth: 50,
                          padding: "6px 6px",
                          fontSize: "0.75rem",
                          lineHeight: 1.2,
                        }}
                        onClick={() => handleToggleShow(film, "media")}
                      >
                        Media
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color={film.show.portal === 1 ? "primary" : "inherit"}
                        sx={{
                          minWidth: 50,
                          padding: "6px 6px",
                          fontSize: "0.75rem",
                          lineHeight: 1.2,
                        }}
                        onClick={() => handleToggleShow(film, "portal")}
                      >
                        Portal
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color={
                          film.show.old_media === 1 ? "primary" : "inherit"
                        }
                        sx={{
                          minWidth: 50,
                          padding: "6px 6px",
                          fontSize: "0.75rem",
                          lineHeight: 1.2,
                        }}
                        onClick={() => handleToggleShow(film, "old_media")}
                      >
                        Dune
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Всплывающее окно с постером */}
        {hoveredPoster && (
          <Box
            sx={{
              position: "absolute",
              top: hoverPosition.top,
              left: hoverPosition.left,
              backgroundColor: "#fff",
              padding: 1,
              boxShadow: 3,
              zIndex: 2000,
            }}
          >
            <img
              src={hoveredPoster}
              alt="Постер фильма"
              width={POSTER_WIDTH}
              height={POSTER_HEIGHT}
            />
          </Box>
        )}

        {/* Модалка */}
        {filmToDelete && (
          <DeleteFilmModal
            film={filmToDelete}
            moviesStore={moviesStore}
            onClose={() => setFilmToDelete(null)}
            open={!!filmToDelete} // Добавляем этот пропс
          />
        )}
      </Box>
    </Box>
  );
};

export default MainBody;
